import type {
	ApiPage,
	ApiQuestion,
	Brandkit,
	FormBrand,
} from "../types/api.types";

// Import all step components
import StepRating from "./steps/step-rating";
import StepNegativeFeedback from "./steps/step-negative-feedback";
import StepWelcome from "./steps/step-welcome";
import StepQuestion from "./steps/step-question";
import StepAboutUser from "./steps/step-about-user";
import StepConsent from "./steps/step-consent";
import StepPositiveThankYou from "./steps/step-positive-thank-you";
import StepNegativeThankYou from "./steps/step-negative-thank-you";
import StepAboutCompany from "./steps/step-about-company";

interface DynamicPageRendererProps {
	brandkit: Brandkit;
	brand: FormBrand;
	page: ApiPage;
	question?: ApiQuestion;
	questions: ApiQuestion[];
	showBackButton?: boolean;
	onBack?: () => void;
	stepKey?: string;
	currentQuestionIndex: number;
	totalQuestions: number;
	totalSteps: number;
	currentStepIndex: number;
	isDarkMode?: boolean;
	isQuestionStep?: boolean;
}

export default function DynamicPageRenderer({
	brandkit,
	brand,
	page,
	question,
	questions: _questions,
	showBackButton,
	onBack,
	stepKey,
	currentQuestionIndex,
	totalQuestions,
	totalSteps,
	currentStepIndex,
	isDarkMode = false,
	isQuestionStep = false,
}: DynamicPageRendererProps) {
	// _questions is available for future use if needed (e.g., question navigation)
	void _questions;
	const baseProps = {
		brandkit,
		brand,
		showBackButton,
		onBack,
		stepKey,
		pageConfig: page,
		isDarkMode,
	};

	// Handle question steps (rendered between welcome and about_user)
	if (isQuestionStep) {
		if (!question) {
			console.warn("Question step rendered without a question");
			return null;
		}
		return (
			<StepQuestion
				{...baseProps}
				question={question}
				questionIndex={currentQuestionIndex}
				totalQuestions={totalQuestions}
				totalSteps={totalSteps}
				currentStepIndex={currentStepIndex}
			/>
		);
	}

	switch (page.type) {
		case "rating":
			return <StepRating {...baseProps} />;

		case "welcome":
			return <StepWelcome {...baseProps} />;

		case "private_feedback":
			return <StepNegativeFeedback {...baseProps} />;

		case "about_company":
			return (
				<StepAboutCompany
					{...baseProps}
					totalSteps={totalSteps}
					currentStepIndex={currentStepIndex}
				/>
			);

		case "about_user":
			return (
				<StepAboutUser
					{...baseProps}
					totalSteps={totalSteps}
					currentStepIndex={currentStepIndex}
				/>
			);

		case "consent":
			return (
				<StepConsent
					{...baseProps}
					totalSteps={totalSteps}
					currentStepIndex={currentStepIndex}
				/>
			);

		case "thank_you":
			return <StepPositiveThankYou {...baseProps} />;

		case "negative_thank_you":
			return <StepNegativeThankYou {...baseProps} />;

		default:
			console.warn(`Unknown page type: ${page.type}`);
			return null;
	}
}
